import { getQuarter, subMonths } from 'date-fns';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { Subscription, catchError, filter, finalize, forkJoin, of } from 'rxjs';
import { DashboardOverrideService } from 'src/app/services/dashboard-override.service';
import { AuthService } from '../../services';
import { CommonService } from '../../services/common.service';
import { DateFilterTypeEnum } from '../../enums/dateFilterType.enum';
import { DateFormatService } from 'src/app/services/dateFormat.service';
import { SelectedDateRange } from 'src/app/models/dateRange.model';
import { LiveOption } from 'src/app/models/liveOption.model';
import { LoaderService } from 'src/app/services/loader.service';
import { Lookup } from 'src/app/models/lookup.model';
import { OrganizationLevel } from '../../models/organizationLevel.model';
import { OrganizationLevelService } from 'src/app/services/organizationLevel.service';
import { ParameterService } from 'src/app/services/parameter.service';
import { TimeZoneModel } from 'src/app/models/timezone.model';
import { ToastService } from 'src/app/services/toast.service';
import { UserProfile } from '../../models/userProfile.model';
import { UserService } from '../../services/user.service';
import { TIME_PREF_12H } from 'src/app/enums/timePreference.enum';
import { MatDialog } from '@angular/material/dialog';
import { UserPreferencesDialogComponent } from 'src/app/modals/user-preferences-dialog/user-preferences-dialog.component';
import { Enum } from 'src/app/models/enum.model';
import { TranslateService } from 'src/app/services/translate.service';
import { toOrganizationTree } from 'src/app/_helpers/org-tree-helper';
import { LocalStorageConstants } from 'src/app/shared/constants/localStorage.constants';
import { readCachedSelection } from 'src/app/shared/utils/cached-selection.util';
import { isDateFilterHiddenRoute } from 'src/app/shared/utils/header-date-filter.util';
import { ACCOUNT_SETTINGS_DIALOG_WIDTH, ACCOUNT_SETTINGS_PANEL_CLASS } from 'src/app/shared/constants/accountSettings.constants';
import { AiAnalystScopeService } from 'src/app/services/ai-analyst-scope.service';
import { AI_ANALYST_ROUTE } from 'src/app/shared/utils/ai-analyst-route.util';
import { getInitials } from 'src/app/shared/utils/initials.util';

@Component({
  standalone: false,
  selector: 'app-header',
  templateUrl: './header.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit, OnChanges, OnDestroy {
  liveOption: LiveOption = { show: false, enabled: false };
  showPreviuosDateRange: boolean;
  parameters: any;
  organizations: OrganizationLevel[];
  organizationsList: OrganizationLevel[];
  @Input() disableMenu: boolean;
  selectedParameter: Lookup;
  userData: UserProfile;
  selectedOrg: OrganizationLevel;
  selectedRange: any;
  routeSub: Subscription;
  showUserPopup: boolean = false;
  showHeaderFilterPopup: boolean = false;
  customDateRanges: Enum[];
  startDate: Date = new Date();
  endDate: Date = new Date();
  prevStartDate: Date = new Date();
  prevEndDate: Date = new Date();
  timeZone: string;
  isDateFilter: boolean = false;
  tenantLogoUrl: string = "";
  timezones: TimeZoneModel[];
  dateFormats: string[];
  timeZoneSub: Subscription;
  today: Date = new Date();
  startDateInput: any;
  endDateInput: any;
  prevStartDateInput: any;
  prevEndDateInput: any;
  orgInitialized: boolean = false;
  riskInitialized: boolean = false;
  disableHeader: any = false;
  is12HourFormat: boolean = false;
  isDateParameterhidden: boolean;
  orgSearchText: string = '';
  isOrgOverridden = false;
  isAiAnalystButtonVisible = false;
  userInitials = '';
  readonly aiAnalystRoute = AI_ANALYST_ROUTE;
  private overrideSub: Subscription;
  private parameterSub: Subscription;
  private aiAnalystSub: Subscription;

  constructor(
    private userService: UserService,
    private authService: AuthService,
    private toastService: ToastService,
    private loaderService: LoaderService,
    private router: Router,
    private commonService: CommonService,
    private parameterService: ParameterService,
    private organizationService: OrganizationLevelService,
    private dateFormatService: DateFormatService,
    private translateService: TranslateService,
    private activatedRoute: ActivatedRoute,
    private dialog: MatDialog,
    private dashboardOverrideService: DashboardOverrideService,
    private aiAnalystScopeService: AiAnalystScopeService
  ) {
    this.customDateRanges = this.commonService.getEnumNameListArray(DateFilterTypeEnum);
  }

  ngOnInit(): void {
    this.initializeSubscriptions();
    this.loadHeaderDropdowns();
    this.initializeRouterEvents();
    this.overrideSub = this.dashboardOverrideService.isAnyOverridden$.subscribe(
      val => (this.isOrgOverridden = val)
    );
  }

  ngOnDestroy(): void {
    this.unsubscribeAll();
    this.overrideSub?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.parameters?.length && !this.organizations?.length) {
      return;
    }

    if (this.parameters?.length > 0 && !this.selectedParameter) {
      this.selectedParameter = this.parameters[0];
      this.commonService.setSelectedParameter(this.selectedParameter);
    }

    if (this.organizations?.length > 0 && !this.selectedOrg) {
      this.selectedOrg = this.organizations[0];
      this.commonService.setSelectedOrg(this.selectedOrg);
    }

    if (!this.riskInitialized && this.parameters?.length > 0) {
      this.riskInitialized = true;
      const selectedParameter = readCachedSelection<Lookup>(LocalStorageConstants.selectedParameter);
      if (selectedParameter) {
        this.setAnalyte(selectedParameter);
      }
    }

    if (!this.orgInitialized && this.organizations?.length > 0) {
      this.orgInitialized = true;
      const selectedOrg = readCachedSelection<OrganizationLevel>(LocalStorageConstants.selectedOrg);
      if (selectedOrg) {
        this.setOrg(selectedOrg);
      }
    }
  }

  private initializeSubscriptions(): void {
    this.commonService.disableHeader.subscribe(this.handleDisableHeader.bind(this));
    forkJoin({
      timezones: this.userService.getAllTimeZones().pipe(catchError(() => of([]))),
      dateFormats: this.userService.getAllDatePreferences().pipe(catchError(() => of([]))),
      logoUrl: this.userService.getTenantLogoUrl().pipe(catchError(() => of(""))),
    }).subscribe(result => {
      this.timezones = result.timezones;
      this.dateFormats = result.dateFormats;
      this.tenantLogoUrl = result.logoUrl || "";
    });
    this.timeZoneSub = this.authService.refreshTimezone.subscribe(refresh => refresh && this.setUserProfile());
    this.parameterSub = this.commonService.selectParameterEmitter.subscribe({
      next: (parameter: Lookup | null) => {
        if (parameter) {
          this.selectedParameter = parameter;
        }
      }
    });
    this.aiAnalystSub = this.aiAnalystScopeService.canUseAiAnalyst$.subscribe({
      next: canUseAiAnalyst => this.isAiAnalystButtonVisible = canUseAiAnalyst
    });
    this.commonService.selectedDateFilterEmitter.subscribe(this.handleDateFilterChange.bind(this));
    this.commonService.liveOptionSubject.subscribe(res => this.liveOption = res);
    this.organizationService.organizationsUpdated.subscribe(res => { if (res) this.getOrganizations(); })
  }

  private initializeRouterEvents(): void {
    const setHiddenHeader = (fullPath: string) => {
      this.isDateParameterhidden = isDateFilterHiddenRoute(fullPath);
    }
    setHiddenHeader(this.router.routerState.snapshot.url);
    this.router.events.pipe(filter(e => e instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.setDateFromParams();
        this.setParameterFromParams();
        this.setOrganizationFromParams();
        setHiddenHeader(event.urlAfterRedirects);
      });
  }

  private unsubscribeAll(): void {
    this.routeSub?.unsubscribe();
    this.timeZoneSub?.unsubscribe();
    this.parameterSub?.unsubscribe();
    this.aiAnalystSub?.unsubscribe();
  }

  private handleDisableHeader(routeData: string): void {
    this.disableHeader = routeData === '/dashboard/device-detail';
  }

  private handleDateFilterChange(dateFilter: SelectedDateRange | null): void {
    if (!dateFilter || !this.selectedRange || dateFilter === this.selectedRange) {
      return;
    }

    if (dateFilter.id === DateFilterTypeEnum.Custom && dateFilter.start && dateFilter.end) {
      this.isDateFilter = false;
      this.startDate = this.dateFormatService.convertTimestampToDate(dateFilter.start);
      this.endDate = this.dateFormatService.convertTimestampToDate(dateFilter.end);
      this.prevStartDate = dateFilter.prevStart ? this.dateFormatService.convertTimestampToDate(dateFilter.prevStart) : this.startDate;
      this.prevEndDate = dateFilter.prevEnd ? this.dateFormatService.convertTimestampToDate(dateFilter.prevEnd) : this.endDate;
      this.initializeDateRangeInputValues();
      this.selectedRange = { ...dateFilter, prevStart: dateFilter.prevStart ?? 0, prevEnd: dateFilter.prevEnd ?? 0 };
      this.setCustomRangeValue();
      return;
    }

    if ((dateFilter.id !== this.selectedRange.id) ||
      (!dateFilter.start && dateFilter.id !== DateFilterTypeEnum.AllDates)) {
      this.setDateRange({ id: dateFilter.id, value: dateFilter.value });
    }
  }

  loadHeaderDropdowns(): void {
    forkJoin({
      parameters: this.parameterService.getAllExcludingNotificationOnly(''),
      organizations: this.organizationService.getAll(''),
    }).subscribe(data => {
      this.parameters = data.parameters;
      this.setInitialParameter();
      this.setOrganization(data.organizations);
      this.setInitialOrganization();
      this.setUserProfile();
    });
  }

  getOrganizations() {
    this.organizationService.getAll().subscribe({
      next: (orgs) => {
        this.setOrganization(orgs);
      },
      error: (error) => {
        this.toastService.showError(error);
      }
    })
  }

  setOrganization(organizations) {
    this.organizationsList = organizations;
    this.organizations = toOrganizationTree(this.organizationsList);
  }

  private setInitialParameter(): void {
    if (this.parameters.length && !this.setParameterFromParams()) {
      const selectedParameter = readCachedSelection<Lookup>(LocalStorageConstants.selectedParameter);
      this.setAnalyte(selectedParameter || this.parameters[0]);
    }
  }

  private setInitialOrganization(): void {
    if (this.organizationsList.length && !this.setOrganizationFromParams()) {
      const selectedOrg = readCachedSelection<OrganizationLevel>(LocalStorageConstants.selectedOrg)
        ?? this.organizationsList.find(org => org.id === this.authService.getCurrentUser().organizationLevelIds?.[0])
        ?? this.organizationsList[0];
      this.setOrg(selectedOrg);
    }
  }

  toggleLive(isEnabled: boolean = true): void {
    this.liveOption.enabled = isEnabled;
    this.commonService.liveOptionSubject.next(this.liveOption);
  }

  inputDateChanged(type: string): void {
    const dateMapping = {
      'from': () => this.startDate = this.startDateInput[0],
      'to': () => this.endDate = this.endDateInput[1],
      'prevFrom': () => this.prevStartDate = this.prevStartDateInput[0],
      'prevTo': () => this.prevEndDate = this.prevEndDateInput[1]
    };
    dateMapping[type]?.();
  }

  setEndDateToNow(): void {
    this.endDate = new Date();
    this.endDateInput = [undefined, this.endDate];
  }

  logout() {
    this.loaderService.show();

    this.authService.logout().pipe(
      finalize(() => this.loaderService.hide())
    ).subscribe({
      next: () => {
        this.toastService.showSuccess('Logged out', true);
        this.router.navigate(['/login']);
      },
      error: () => { }
    });
  }

  showUserPopupFn() {
    this.showUserPopup = !this.showUserPopup;
  }

  showHeaderFilterFn() {
    this.showHeaderFilterPopup = !this.showHeaderFilterPopup;
  }

  setAnalyte(parameter: any) {
    this.commonService.setSelectedParameter(parameter);
  }

  private applyUserProfile(profile: UserProfile): void {
    this.is12HourFormat = profile.timePreference === TIME_PREF_12H;
    this.authService.setCurrentUserPreferences(profile);
    this.authService.setCurrentUserAiAnalystEnabled(profile.isAiAnalystEnabled);
    this.dateFormatService.setUserDatePreferences(profile);
    this.translateService.changeLanguage(this.authService.getLanguagePreference());
  }

  private setUserData(profile: UserProfile): void {
    this.userData = profile;
    this.userInitials = getInitials(profile?.fullName);
  }

  private setUserProfile() {
    this.userService.getCurrentUserProfile().subscribe((data) => {
      this.setUserData(data);
      const currentUser = this.authService.getCurrentUser();
      this.timeZone = currentUser.userTimeZone;
      this.applyUserProfile(this.userData);

      const storedRange: string | null = localStorage.getItem(LocalStorageConstants.selectedDateRange);
      if (this.setDateFromParams()) {
        return;
      }

      const range: SelectedDateRange | null = this.commonService.selectedDateFilterEmitter.getValue() ?? (storedRange ? JSON.parse(storedRange) : null);
      if (range) {
        this.startDate = range.start ? this.dateFormatService.convertTimestampToDate(range.start) : null;
        this.endDate = range.end ? this.dateFormatService.convertTimestampToDate(range.end) : null;
        this.prevStartDate = range.prevStart ? this.dateFormatService.convertTimestampToDate(range.prevStart) : null;
        this.prevEndDate = range.prevEnd ? this.dateFormatService.convertTimestampToDate(range.prevEnd) : null;

        this.setDateRange({ id: range.id, value: range.value }, false);

        if (range.id === DateFilterTypeEnum.Custom && this.startDate && this.endDate &&
          (!this.showPreviuosDateRange || (this.prevStartDate && this.prevEndDate))) {
          this.initializeDateRangeInputValues();
          this.setCustomRangeValue();
          this.updateSelectedRange(range, this.startDate, this.endDate, this.prevStartDate, this.prevEndDate);
        }
      } else {
        this.setDateRange({
          id: DateFilterTypeEnum.ThisYear,
          value: this.commonService.getEnumName(DateFilterTypeEnum, DateFilterTypeEnum.ThisYear)
        });
      }
    });
  }

  setOrg(org) {
    this.selectedOrg = org;
    localStorage.setItem(LocalStorageConstants.selectedOrg, JSON.stringify(org));
    this.commonService.setSelectedOrg(this.selectedOrg);
  }

  setDateRange(range: any, showFilter: boolean = true, isInteraction = false) {
    const now = new Date();
    this.isDateFilter = false;

    const updateRange = (start: Date | null, end: Date | null) => {
      this.updateSelectedRange(range, start, end, null, null);
    };

    switch (range.id) {
      case 0:
        this.today = now;
        this.isDateFilter = showFilter;
        this.selectedRange = { value: range.value, id: range.id };
        break;
      case 1:
        updateRange(new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0), now);
        break;
      case 2: /* this week */
        updateRange(new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay()), now);
        break;
      case 3: /* this month */
        updateRange(new Date(now.getFullYear(), now.getMonth()), now);
        break;
      case 4: /* this quarter */ {
        const q = getQuarter(now);
        updateRange(new Date(now.getFullYear(), (q * 3) - 3), new Date(now.getFullYear(), (q * 3), 0, 23, 59, 59));
        break;
      }
      case 5: /* this year */
        updateRange(new Date(now.getFullYear(), 0), now);
        break;
      case 6: /* last week */
        updateRange(new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay() - 7), new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay() - 1, 23, 59, 59));
        break;
      case 7: /* last month */
        updateRange(new Date(now.getFullYear(), now.getMonth() - 1, 1), new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59));
        break;
      case 8: /* last quarter */ {
        const currentQuarterStartDate = new Date(now.getFullYear(), (getQuarter(now) * 3) - 3);
        updateRange(
          subMonths(currentQuarterStartDate, 3),
          new Date(currentQuarterStartDate.getFullYear(), currentQuarterStartDate.getMonth(), 0, 23, 59, 59)
        );
        break;
      }
      case 9: /* last year */
        updateRange(new Date(now.getFullYear() - 1, 0), new Date(now.getFullYear() - 1, 12, 0, 23, 59, 59));
        break;
      case 10:
        updateRange(null, null);
        break;
    }

    if (isInteraction && range.id !== DateFilterTypeEnum.Custom) {
      this.toggleLive(false);
    }
  }

  updateSelectedRange(range: any, start: Date | null, end: Date | null, prevStart: Date | null, prevEnd: Date | null) {
    this.selectedRange = {
      value: range.value,
      id: range.id,
      start: start ? this.dateFormatService.convertDateToTimestamp(start) : 0,
      end: end ? this.dateFormatService.convertDateToTimestamp(end) : 0,
      prevStart: prevStart ? this.dateFormatService.convertDateToTimestamp(prevStart) : 0,
      prevEnd: prevEnd ? this.dateFormatService.convertDateToTimestamp(prevEnd) : 0,
    };
    localStorage.setItem(LocalStorageConstants.selectedDateRange, JSON.stringify(this.selectedRange));
    this.commonService.selectedDateFilter(this.selectedRange);
    this.setCustomRangeValue();
  }

  setCustomRangeValue() {
    const dateString = (date: Date) => this.dateFormatService.formatDateTimeToString(date, this.dateFormatService.userDateWithTimeFormat);
    if (this.selectedRange?.id === DateFilterTypeEnum.Custom) {
      const prevRange = this.showPreviuosDateRange ? `<br> ${dateString(this.prevStartDate)} - ${dateString(this.prevEndDate)}` : '';
      this.selectedRange.value = `<b>${dateString(this.startDate)} - ${dateString(this.endDate)}</b> ${prevRange}`;
    }
  }

  applyFilter(isInteraction = false) {
    this.isDateFilter = false;
    const range = { id: DateFilterTypeEnum.Custom, value: this.commonService.getEnumName(DateFilterTypeEnum, DateFilterTypeEnum.Custom) };
    this.updateSelectedRange(range, this.startDate, this.endDate, this.prevStartDate, this.prevEndDate);
    if (isInteraction) this.toggleLive(false);
  }

  routeToLandingPage() {
    const url = this.authService.getLandingPageUrl();
    this.router.navigate([url]);
  }

  setParameterFromParams() {
    const { selectedParameterId } = this.activatedRoute.snapshot.queryParams;
    if (selectedParameterId) {
      const selectedParameter = this.parameters.find(parameter => parameter.id === selectedParameterId);
      if (selectedParameter) {
        this.setAnalyte(selectedParameter);
        return true;
      }
    }
    return false;
  }

  setOrganizationFromParams() {
    const { selectedOrgId } = this.activatedRoute.snapshot.queryParams;
    if (selectedOrgId) {
      const selectedOrg = this.organizationsList.find(org => org.id === selectedOrgId);
      if (selectedOrg) {
        this.setOrg(selectedOrg);
        return true;
      }
    }
    return false;
  }

  setDateFromParams() {
    const { dateFilterId, startDate, endDate, prevStartDate, prevEndDate } = this.activatedRoute.snapshot.queryParams;
    const dateFilterIdNum = Number(dateFilterId);

    if (dateFilterIdNum && dateFilterIdNum !== DateFilterTypeEnum.Custom) {
      this.setDateRange({ value: DateFilterTypeEnum[dateFilterIdNum], id: dateFilterIdNum });
      return true;
    } else if (dateFilterIdNum === DateFilterTypeEnum.Custom && startDate && endDate) {
      const setDate = (date) => this.dateFormatService.convertTimestampToDate(Number(date));
      this.startDate = setDate(startDate);
      this.endDate = setDate(endDate);
      this.prevStartDate = prevStartDate ? setDate(prevStartDate) : new Date();
      this.prevEndDate = prevEndDate ? setDate(prevEndDate) : new Date();
      this.initializeDateRangeInputValues();
      this.applyFilter();
      return true;
    }
    return false;
  }

  initializeDateRangeInputValues() {
    this.startDateInput = [this.startDate, undefined];
    this.endDateInput = [undefined, this.endDate];
    this.prevStartDateInput = [this.prevStartDate, undefined];
    this.prevEndDateInput = [undefined, this.prevEndDate];
  }

  openUserPreferencesDialog(): void {
    this.dialog.open(UserPreferencesDialogComponent, {
      width: ACCOUNT_SETTINGS_DIALOG_WIDTH,
      maxWidth: 'calc(100vw - 32px)',
      panelClass: ACCOUNT_SETTINGS_PANEL_CLASS,
      data: {
        userPreferences: this.userData,
        timezones: this.timezones,
        dateFormats: this.dateFormats
      }
    }).afterClosed().subscribe((saved: UserProfile | undefined) => {
      if (!saved) return;
      this.setUserData(saved);
      this.applyUserProfile(saved);
    });
  }

  closeUserPopup() {
    this.showUserPopup = false;
  }

  trackByEnumValue(_: number, item: Enum): string {
    return item.value;
  }

  trackByOrgLevelId(_: number, org: OrganizationLevel): string {
    return org.id;
  }

  trackByLookupId(_: number, item: Lookup): number | string {
    return item.id;
  }

}
